import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Button,
  Chip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  PhoneInTalk,
  Add,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  getAdminVoicePackages,
  createVoicePackage,
  updateVoicePackage,
  deleteVoicePackage,
  toggleVoicePackageActive,
} from '../services/voicePackageApi';
import VoicePackagesFilters from '../components/admin/VoicePackagesFilters';
import VoicePackagesTable from '../components/admin/VoicePackagesTable';
import VoicePackageForm from '../components/admin/VoicePackageForm';

const ITEMS_PER_PAGE = 50;

const AdminVoicePackagesPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Data
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  // Filters & pagination
  const [filters, setFilters] = useState({ country: '', search: '' });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  // UI state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formOpen, setFormOpen] = useState(false);
  const [formEntry, setFormEntry] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, entry: null });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Fetch packages
  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: ITEMS_PER_PAGE,
      };
      if (filters.country) params.country = filters.country;
      if (debouncedSearch) params.search = debouncedSearch;

      const res = await getAdminVoicePackages(params);
      const payload = res.data;
      setEntries(payload?.data || []);
      setTotal(payload?.total || 0);
      setTotalPages(payload?.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch voice packages:', err);
      setSnackbar({
        open: true,
        message: t('voicePackages.fetchError', 'Failed to load voice packages'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, filters.country, debouncedSearch, t]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  // Handlers
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    if (field !== 'search') setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({ country: '', search: '' });
    setPage(1);
  };

  const handleSortChange = (field) => {
    if (sortField === field) {
      if (sortOrder === 'asc') setSortOrder('desc');
      else if (sortOrder === 'desc') {
        setSortField('');
        setSortOrder('');
      }
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleFormSave = async (payload, existingId) => {
    if (existingId) {
      await updateVoicePackage(existingId, payload);
      setEntries((prev) =>
        prev.map((e) => (e.id === existingId ? { ...e, ...payload } : e))
      );
      setSnackbar({
        open: true,
        message: t('voicePackages.updated', 'Package updated'),
        severity: 'success',
      });
    } else {
      await createVoicePackage(payload);
      setSnackbar({
        open: true,
        message: t('voicePackages.created', 'Package created'),
        severity: 'success',
      });
      fetchCatalog();
    }
  };

  const handleDelete = async () => {
    const entry = deleteDialog.entry;
    if (!entry) return;
    try {
      await deleteVoicePackage(entry.id);
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      setTotal((prev) => prev - 1);
      setSnackbar({
        open: true,
        message: t('voicePackages.deleted', 'Package deleted'),
        severity: 'success',
      });
    } catch (err) {
      console.error('Failed to delete package:', err);
      setSnackbar({
        open: true,
        message: t('voicePackages.deleteError', 'Failed to delete package'),
        severity: 'error',
      });
    } finally {
      setDeleteDialog({ open: false, entry: null });
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await toggleVoicePackageActive(id);
      setEntries((prev) =>
        prev.map((e) =>
          e.id === id ? { ...e, is_active: !e.is_active } : e
        )
      );
    } catch (err) {
      console.error('Failed to toggle active:', err);
      setSnackbar({
        open: true,
        message: t('voicePackages.toggleError', 'Failed to toggle active status'),
        severity: 'error',
      });
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      {/* Header AppBar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: '#fff',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        <Toolbar>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
          >
            {t('common.backToCalculator', 'Back to Calculator')}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneInTalk sx={{ color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">
              {t('voicePackages.title', 'Voice Packages')}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ p: 4 }}>
        {/* Page header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            {t('voicePackages.title', 'Voice Packages')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {t('voicePackages.subtitle', 'Manage voice traffic minute packages')}
          </Typography>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setFormEntry(null);
                setFormOpen(true);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#00C853',
                '&:hover': { bgcolor: '#00A843' },
                borderRadius: 1,
              }}
            >
              {t('voicePackages.addPackage', 'Add Package')}
            </Button>

            <Box sx={{ flex: 1 }} />

            <Chip
              label={t('voicePackages.showingEntries', 'Showing {{showing}} of {{total}} entries', {
                showing: entries.length,
                total,
              })}
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: 12,
                bgcolor: 'white',
                border: '1px solid #e5e7eb',
              }}
            />
          </Box>
        </Box>

        {/* Filters */}
        <VoicePackagesFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />

        {/* Table */}
        <VoicePackagesTable
          entries={entries}
          loading={loading}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          onEditClick={(entry) => {
            setFormEntry(entry);
            setFormOpen(true);
          }}
          onDeleteClick={(entry) => setDeleteDialog({ open: true, entry })}
          onToggleActive={handleToggleActive}
          sortField={sortField}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
        />
      </Box>

      {/* Add/Edit Form Dialog */}
      <VoicePackageForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setFormEntry(null);
        }}
        onSave={handleFormSave}
        entry={formEntry}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, entry: null })}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t('voicePackages.confirmDelete', 'Delete Package?')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'voicePackages.confirmDeleteText',
              'Are you sure you want to delete "{{name}}" ({{country}})?',
              {
                name: deleteDialog.entry?.name || '',
                country: deleteDialog.entry?.country || '',
              }
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, entry: null })}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminVoicePackagesPage;
